/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [APP-LINK] THE PUBLIC "SEND ME THE APP" REQUEST.
 *
 *  Anybody may open `/download-app` on the dashboard's origin, type a mobile number and pick a
 *  phone type. If the number belongs to an officer who may sign in, that number receives ONE SMS
 *  carrying the KP Duty Mitra store link for the chosen platform. Whatever the number, the caller
 *  is told the same sentence — `APP_LINK_ACCEPTED_MESSAGE` — and nothing else.
 *
 *  ⚠️ THIS FILE IMPORTS NOTHING, because the `constants` barrel is the PWA's zod-free and
 *  luxon-free runtime surface (CLAUDE.md §8 [P5b]).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * The phone types a link exists for, lower-case to match the one platform spelling this API
 * already carries (`verifyOtpSchema.platform`). ⚠️ That tuple is a DIFFERENT concept — the FCM
 * client's platform, `web` and `admin` included — so this one is its own, and the two must not be
 * merged: an `admin` app link means nothing.
 */
export const APP_LINK_PLATFORMS = ['android', 'ios'] as const;
export type AppLinkPlatform = (typeof APP_LINK_PLATFORMS)[number];

/** What a request that names no platform is sent: the client's stated default. */
export const APP_LINK_DEFAULT_PLATFORM: AppLinkPlatform = 'android';

/**
 * ⚠️ THE ONE ANSWER, FOR EVERY NUMBER. The 202 carries it and the page renders it, so a
 * registered officer, a deactivated one, a stranger and a typo all read the same words. It never
 * says "sent" — it says what WILL happen IF the number is registered, which is all the caller may
 * learn. `POST /auth/request-otp` is an enumeration oracle by a client decision ([P4-UX.1]); this
 * endpoint is deliberately not, so nobody may copy that precedent here.
 */
export const APP_LINK_ACCEPTED_MESSAGE =
  'If this number is registered, an SMS with the link to download KP Duty Mitra will be sent to it.';

/**
 * ⚠️ A CLIENT-MINTED DEVICE ID — `rotationKey`'s band, for `rotationKey`'s reasons: charset-
 * restricted, length-BANDED rather than fixed so the encoding is not frozen into the server, and the
 * 64-character ceiling is what stops an unbounded string reaching a counter key.
 *
 * It keys the per-device meter and NOTHING else. The client controls it, so it is a fairness
 * convenience for officers sharing one police-station network and never a control: the per-address
 * and per-number meters are the ones a caller cannot shed.
 */
export const APP_LINK_DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{22,64}$/;
